import { useId, type MouseEvent } from "react";
import { Link } from "react-router";
import markUrl from "../../assets/logo/giraffe-mark.svg";
import type { User } from "../../shared/api/types";
import { AiBadge } from "../../shared/components/AiBadge";
import { PersonAvatar } from "../../shared/components/PersonAvatar";
import { WorkTypeTile } from "../../shared/components/WorkTypeTile";
import { aiText } from "../../shared/i18n/aiText";
import { formatDate, formatDateTime, formatRelative } from "../../shared/lib/format";
import { pickStatusName, useLocale } from "../../shared/lib/locale-context";
import { userLabel } from "../../shared/lib/personLabel";
import { langFor } from "../../shared/lib/thai";
import { notificationText } from "./notificationText";
import { isUnread, LATEST_WINDOW_MS, rowLine, spokenAge, type Notification } from "./rowModel";

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 4.6V8l2.4 1.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * One notice (design §3.1): the whole row is a link to the item (Ctrl/Cmd-click and middle-click open a new tab), the unread
 * dot is a separate button after it (never nested in the link). Summary and status are the item's live values; the excerpt
 * is plain text the server computed through the reader's own visibility.
 */
export function NotificationRow({
  notification,
  directory,
  excerptLines = 3,
  onOpen,
  onToggleRead,
  now = new Date(),
  rowRef,
}: {
  notification: Notification;
  directory: Map<number, User>;
  excerptLines?: number;
  /** A plain click (same tab): the caller closes the panel; the link navigates. */
  onOpen: (notification: Notification, sameTab: boolean) => void;
  onToggleRead: (notification: Notification) => void;
  now?: Date;
  rowRef?: (element: HTMLAnchorElement | null) => void;
}) {
  const locale = useLocale();
  const excerptId = useId();
  const unread = isUnread(notification);
  const line = rowLine(notification, now);
  const actorUser = notification.actorId === null || notification.actorId === undefined ? undefined : directory.get(notification.actorId);
  const actorName = actorUser ? userLabel(actorUser) : notificationText("tfgTeam");
  const old = now.getTime() - Date.parse(notification.createdAt) > LATEST_WINDOW_MS;
  const shortAge = old ? formatDate(notification.createdAt, locale, now) : formatRelative(notification.createdAt, now);
  const age = old ? shortAge : spokenAge(notification.createdAt, now);
  const item = notification.item ?? null;
  const statusName = item ? pickStatusName(locale, item.status) : null;
  // Batch 23 (UX-B23 §5): a role notice's tag ("As tester") is part of what the row says, spoken after the line.
  const tag = line.kind === "actor" ? line.tag : undefined;
  const lineText = line.kind === "actor" ? `${actorName} ${line.phrase}${tag ? `, ${tag}` : ""}` : line.text;

  // "Unread. <Actor> mentioned you in a comment, 4 days ago. BMS-6392, <summary>, Support Investigating." (design §8)
  const name = [
    unread ? notificationText("unreadPrefix") : null,
    `${lineText}${notification.actorChannel === "ai" && line.kind === "actor" ? `, ${aiText("badgeLabel", locale)}` : ""}, ${age}.`,
    item ? `${item.key}, ${item.summary}${statusName ? `, ${statusName}` : ""}.` : null,
  ]
    .filter(Boolean)
    .join(" ");

  function onClick(event: MouseEvent<HTMLAnchorElement>) {
    const sameTab = event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
    onOpen(notification, sameTab);
  }

  return (
    <li className={unread ? "gf-notif unread" : "gf-notif"} data-notification-id={notification.id}>
      <Link
        ref={rowRef}
        to={notification.linkPath}
        className="gf-notif-link"
        aria-label={name}
        aria-describedby={notification.excerpt ? excerptId : undefined}
        onClick={onClick}
        onAuxClick={(event) => {
          if (event.button === 1) onOpen(notification, false);
        }}
      >
        <span className="gf-notif-av" aria-hidden="true">
          {line.kind === "actor" ? (
            <PersonAvatar className="gf-av gf-notif-face" name={actorName} avatarUrl={actorUser?.avatarUrl} isAgent={actorUser?.isAgent === true} />
          ) : (
            <img className="gf-notif-face gf-notif-system" src={markUrl} alt="" width={32} height={32} />
          )}
        </span>
        <span className="gf-notif-body">
          <span className="gf-notif-l1">
            {line.kind === "actor" ? (
              <>
                <b lang={langFor(actorName)}>{actorName}</b>
                <AiBadge channel={notification.actorChannel} /> {line.phrase}
                {tag && (
                  <>
                    {" "}
                    <span className="gf-notif-tag" data-testid="notification-role-tag">
                      {tag}
                    </span>
                  </>
                )}
              </>
            ) : line.kind === "system" ? (
              <span className={`gf-notif-sla ${line.tone}`}>
                <ClockIcon />
                <b>{line.text}</b>
              </span>
            ) : (
              <b lang={langFor(line.text)}>{line.text}</b>
            )}
            {" · "}
            <time className="gf-notif-time" dateTime={notification.createdAt} title={formatDateTime(notification.createdAt, locale, now)}>
              {shortAge}
            </time>
          </span>
          {item && (
            <span className="gf-notif-l2">
              <WorkTypeTile type={item.type} hideLabel />
              <span className="gf-notif-summary" lang={langFor(item.summary)}>
                {item.summary}
              </span>
            </span>
          )}
          {item && (
            <span className="gf-notif-l3">
              <span className="gf-key">{item.key}</span>
              {statusName && (
                <>
                  {" • "}
                  <span lang={langFor(statusName)}>{statusName}</span>
                </>
              )}
            </span>
          )}
          {notification.excerpt && (
            <span className="gf-notif-excerpt" id={excerptId} style={{ WebkitLineClamp: excerptLines }}>
              {notification.excerpt.isInternal && <span className="gf-notif-internal">{notificationText("internalNote")}</span>}
              <span lang={langFor(notification.excerpt.text)}>{notification.excerpt.text}</span>
            </span>
          )}
        </span>
      </Link>
      <button
        type="button"
        className="gf-notif-dot"
        aria-label={unread ? notificationText("markRead") : notificationText("markUnread")}
        title={unread ? notificationText("markRead") : notificationText("markUnread")}
        onClick={(event) => {
          event.stopPropagation();
          onToggleRead(notification);
        }}
      >
        <span aria-hidden="true" />
      </button>
    </li>
  );
}
